import { useCallback, useMemo, useState } from 'react';
import cn from 'classnames';
import { connect, useDispatch, useSelector } from 'react-redux';
import { differenceInSeconds } from 'date-fns';

import { IDictionarySubjectTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectTypeSelectOption';
import { IDictionarySubjectKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectKindSelectOption';
import { IDictionarySubjectSubKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectSubKindSelectOption';
import { IDictionaryRiskCategorySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryRiskCategorySelectOption';
import { IDictionaryDangerClassSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryDangerClassSelectOption';
import { IKnmErknmFormObject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormObject';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { KnmAddressField } from 'KnmPackage/MainModule/components/KnmAddressField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { InspectionResultFieldEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultFieldEnum';
import { ErknmInspectionResultMessage } from 'KnmErknmPackage/MainModule/components/ErknmInspectionResultMessageMessage';
import { InspectionResultActionTypeEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultActionTypeEnum';
import {
  RELOAD_OBJECT_BY_ERVK,
  RELOAD_OBJECT_BY_ERVK_TIME_OUT,
  RELOAD_OBJECT_WARNING,
} from 'CorePackage/MainModule/constants/notices';

import styles from './KnmObjectItem.module.scss';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { KnmSystemTypesEnum } from '../../MainModule/enums/KnmSystemTypesEnum';
import { Tooltip } from '../../../UIPackage/MainModule/components/Tooltip';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { getUserRoleIsProsecutorOrSuperProsecutor } from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IDecisionForm } from '../../../DecisionPackage/MainModule/interfaces/IDecisionForm';
import { IDecisionFormRef } from '../../../DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { composeValidators } from '../../../CorePackage/MainModule/validators/composeValidators';
import { ErknmFieldMark } from '../../../KnmErknmPackage/MainModule/components/ErknmFieldMark';
import { ErvkCorrectionRequiredInfo } from '../../../KnmErknmPackage/MainModule/components/ErvkCorrectionRequiredInfo';
import { ErknmApproveRemarkChanges } from '../../../KnmErknmPackage/ErknmApproveRemarkModule/components/ErknmApproveRemarkChanges';
import { ErknmApproveRemarkSectionCodeEnum } from '../../../KnmErknmPackage/ErknmApproveRemarkModule/enums/ErknmApproveRemarkSectionCodeEnum';
import { KnmLateFieldsRemark } from '../../KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { knmReload } from '../../MainModule/actions/knmActions';
import { decisionGet } from '../../../DecisionPackage/MainModule/actions/decisionActions';
import { updateKnmControlObjects } from '../../../APIPackage/KnmModule/services/knmSignApi';
import { TextInputFieldAsLink } from '../../../UIPackage/MainModule/components/TextInputFieldAsLink';
import { getKnmDataId } from '../../MainModule/selectors/knmSelectors';
import {
  getDecisionDataCreatedAt,
  getDecisionDataId,
} from '../../../DecisionPackage/MainModule/selectors/decisionSelectors';
import { Hint } from '../../../CorePackage/MainModule/components/Hint';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';
import { useObjectFieldsDisabled } from '../common/useObjectFieldsDisabled';
import { updateDecisionControlObjects } from '../../../APIPackage/DecisionModule/services/decisionApi';

interface IErknmObjectProps {
  addOnlyProductionObject?: boolean | undefined;
  created: string | null | undefined;
  dangerClassesSelectOptions: IDictionaryDangerClassSelectOption[] | undefined;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  hazardClassApplies: boolean | undefined;
  id: string;
  index: number;
  invalidFields?: string[];
  isCanRefreshControlObject?: boolean;
  isErknm: boolean;
  isImpossibilityOfPrevious?: boolean;
  isLinkedByImpossibility?: boolean;
  isPm: boolean;
  isWatchOnly: boolean;
  knmSystemType?: KnmSystemTypesEnum;
  name: string;
  objectKindSelectOptionsSelectorFunction?: (
    state: IRootState,
    objectTypeDictVersionId?: string,
  ) => IDictionarySubjectKindSelectOption[] | undefined;
  objectSubKindSelectOptionsSelectorFunction?: (
    state: IRootState,
    objectTypeDictVersionId?: string,
  ) => IDictionarySubjectSubKindSelectOption[] | undefined;
  objectKindChangeCallback?: () => void;
  objectTypeChangeCallback?: (value: IDictionarySubjectTypeSelectOption) => void;
  objectKindsSelectOptions: IDictionarySubjectKindSelectOption[] | undefined;
  objectSubKindsSelectOptions: IDictionarySubjectSubKindSelectOption[] | undefined;
  objectTypesSelectOptions?: IDictionarySubjectTypeSelectOption[] | undefined;
  onRemove: () => void;
  riskCategoriesSelectOptions: IDictionaryRiskCategorySelectOption[] | undefined;
  value: IKnmErknmFormObject;
  isFilledFromPrescriptionLink?: boolean;
}

interface IErknmObjectStoreProps {
  decisionDataCreated?: string | null;
  decisionId?: number;
  isProsecutorOrSuperProsecutor: boolean;
  knmId?: number;
  settings: IAdminSetting[] | undefined;
}

const KnmObjectWithoutStore = ({
  addOnlyProductionObject,
  created,
  dangerClassesSelectOptions,
  formRef,
  hazardClassApplies,
  id,
  index,
  invalidFields,
  isCanRefreshControlObject = false,
  isErknm,
  isImpossibilityOfPrevious,
  isLinkedByImpossibility,
  isPm,
  isWatchOnly,
  knmSystemType,
  name,
  objectKindChangeCallback,
  objectTypeChangeCallback,
  objectKindSelectOptionsSelectorFunction,
  objectSubKindSelectOptionsSelectorFunction,
  objectKindsSelectOptions,
  objectSubKindsSelectOptions,
  objectTypesSelectOptions,
  onRemove,
  riskCategoriesSelectOptions,
  value,
  decisionDataCreated,
  decisionId,
  isProsecutorOrSuperProsecutor,
  knmId,
  settings,
  isFilledFromPrescriptionLink,
}: IErknmObjectProps & IErknmObjectStoreProps) => {
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(true);
  const dispatch = useDispatch();

  const objectKindsSelectOptionsByObjectType = useSelector((state: IRootState) =>
    value?.objectType?.value?.dictVersionId && objectKindSelectOptionsSelectorFunction
      ? objectKindSelectOptionsSelectorFunction(state, value?.objectType?.value?.dictVersionId)
      : undefined,
  );

  const objectSubKindsSelectOptionsByObjectType = useSelector((state: IRootState) =>
    value?.objectType?.value?.dictVersionId && objectSubKindSelectOptionsSelectorFunction
      ? objectSubKindSelectOptionsSelectorFunction(state, value?.objectType?.value?.dictVersionId)
      : undefined,
  );

  const isNewObject = value.isNew;
  const hasId: boolean = !!value.ervkId || !!value.cosId;

  const isUseCosIdentifiers = isLaterThanAdminSetting(
    created || decisionDataCreated,
    settings,
    AdminSettingCodesEnum.COS_IDENTIFIERS_2025_4_1_2,
  );

  const isUseOnlyLowRiskInManualObject = isLaterThanAdminSetting(
    created || decisionDataCreated,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_223_4_1_42_STARTS_FROM,
  );

  const isHideRiskCategoryOrDangerClass = isLaterThanAdminSetting(
    created || decisionDataCreated,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_4_STARTS_FROM,
  );

  const isLaterThanHideFields4129 = isLaterThanAdminSetting(
    created || decisionDataCreated,
    settings,
    AdminSettingCodesEnum.SETTING_HIDE_FIELDS_4_1_29,
  );

  const {
    isRiskCategoryDisabled,
    isObjectKindDisabled,
    isRefreshButtonDisabled,
    isObjectTypeDisabled,
    isObjectSubKindDisabled,
    isAddressDisabled,
    isDangerClassDisabled,
  } = useObjectFieldsDisabled({
    value,
    isWatchOnly: isWatchOnly || isImpossibilityOfPrevious === true || isFilledFromPrescriptionLink === true,
    isErknm,
    invalidFields,
    created,
    dangerClassesSelectOptions,
    objectKindsSelectOptions: hasId ? objectKindsSelectOptions : objectKindsSelectOptionsByObjectType,
    objectTypesSelectOptions,
    riskCategoriesSelectOptions,
    canRefreshControlObject: isCanRefreshControlObject,
    hazardClassApplies,
  });

  const lateFieldName = isPm ? `/objects/${value.guid}` : '';

  const addressFieldName =
    !isErknm && !isPm ? (`${name}.address` as keyof IDecisionForm) : (`${name}.address` as keyof IKnmForm);
  const objectNameFieldName =
    !isErknm && !isPm ? (`${name}.name` as keyof IDecisionForm) : (`${name}.name` as keyof IKnmForm);

  const numberFieldNameTemplate = isUseCosIdentifiers || value.cosId ? `${name}.cosId` : `${name}.ervkNumber`;
  const ervkNumberFieldName =
    !isErknm && !isPm ? (numberFieldNameTemplate as keyof IDecisionForm) : (numberFieldNameTemplate as keyof IKnmForm);
  const tmpErvkCosIdFieldName =
    !isErknm && !isPm ? (`${name}.ervkNumber` as keyof IDecisionForm) : (`${name}.tmpErvkCosId` as keyof IKnmForm);

  const autocompleteFieldName = () => {
    if (isErknm) {
      return `${name}.autocomplete` as keyof IKnmForm;
    }
    if (isPm) {
      return `${name}.organizationAutocomplete` as keyof IKnmForm;
    }

    return `${name}.organizationAutocomplete` as keyof IDecisionForm;
  };

  const inspectionResultAddress = value?.inspectionResults?.find(
    i => i.inspectedField === InspectionResultFieldEnum.OBJECT_ADDRESS,
  );
  const inspectionResultAvailable = value?.inspectionResults?.find(
    i => i.inspectedField === InspectionResultFieldEnum.OBJECT_AVAILABLE,
  );
  const inspectionResultName = value?.inspectionResults?.find(
    i => i.inspectedField === InspectionResultFieldEnum.OBJECT_NAME,
  );

  const shouldShowDangerClass =
    (!value.ervkId && isHideRiskCategoryOrDangerClass && hazardClassApplies) ||
    !isHideRiskCategoryOrDangerClass ||
    (value.ervkId && hazardClassApplies);

  const TooltipPartText = () => {
    if (isPm) {
      return 'Основания проведения профилактических мероприятий';
    }
    if (isErknm) {
      return 'Основания проведения КНМ';
    }

    return '';
  };

  const handleAddressFieldBlur = useCallback(
    // @ts-ignore
    (addressValue: string) => formRef?.current.change(addressFieldName, addressValue),
    [addressFieldName, formRef],
  );

  const handleAutocompleteClear = useCallback(() => {
    // @ts-ignore
    formRef?.current.change(autocompleteFieldName(), false);
  }, [autocompleteFieldName, formRef]);

  const handleObjectKindChange = useCallback(
    (selectValue?: IDictionarySubjectKindSelectOption) => {
      if (objectKindChangeCallback) {
        objectKindChangeCallback();
      }
      if (selectValue?.analogue) {
        if (!isErknm && !isPm) {
          // @ts-ignore
          formRef?.current.change(`${name}.objectSubKind` as keyof IDecisionForm, selectValue);
        } else {
          // @ts-ignore
          formRef?.current.change(`${name}.objectSubKind` as keyof IKnmForm, selectValue);
        }
      }
    },
    [formRef, name, isErknm, isPm],
  );

  /*
    Запрос обновления данных по объекту контроля и перезапрос паспорта
   */
  const refresh = useCallback(() => {
    if (value.cosId) {
      const isKnm = isErknm || isPm;
      const passportId = isKnm ? knmId : decisionId;

      const updateControlObjects = isKnm ? updateKnmControlObjects : updateDecisionControlObjects;

      try {
        updateControlObjects(Number(passportId), [value.cosId]).then(() => {
          if (!isErknm && !isPm) {
            dispatch(decisionGet());
          } else {
            dispatch(knmReload());
          }
        });
      } catch (e) {
        console.error('Ошибка обновления данных контрольного объекта', e);
      }
    }
  }, [isPm, isErknm, knmId, decisionId]);

  // Время отправки последнего перезапроса
  const updatingTime = useMemo(() => {
    return value?.updating || null;
  }, [value]);

  // Истекло ли время ожидания ответа
  const isUpdatingTimeOut = useMemo(() => {
    if (!updatingTime) return false;
    const cleanString = updatingTime.replace(/\[.*]/, '');
    const target = new Date(cleanString);
    const now = new Date();

    // return differenceInMinutes(now, target) >= 30;
    // TODO на время демонстрации
    return differenceInSeconds(now, target) >= 15;
  }, [updatingTime]);

  /**
   * Генерация имени гармошки объекта
   */
  const generateCollapseName = useMemo(() => {
    if (inspectionResultAvailable) {
      return <ErknmInspectionResultMessage inspectionResult={inspectionResultAvailable} />;
    }

    if (typeof value.ervkId === 'number' || value.cosId) {
      if (isUseCosIdentifiers || value.cosId) {
        return `${value.cosId ? `(${value.cosId})` : ''} ${value.name || ''}`;
      }

      if (isProsecutorOrSuperProsecutor) {
        const s = value.tmpErvkCosId ? `(${value.tmpErvkCosId})` : null;

        return `${s || `(${value.ervkNumber})`} ${value.name || ''}`;
      }

      return `${value.ervkNumber ? `(${value.ervkNumber})` : ''} ${value.name || ''}`;
    }

    if (value.manualAddress) {
      return `${value.region?.label || ''} ${value.manualAddress || ''}`;
    }

    return `${value.fiasRegion?.label || ''} ${value.address || ''}`;
  }, [value, inspectionResultAvailable]);

  const [isOpen, setIsOpen] = useState(isNewObject);

  const toggle = useCallback(() => setIsOpen(!isOpen), [isOpen]);

  const hasError = checkHasErrorInCollapseBlock(formRef, name);

  const placeLabel = isLaterThanHideFields4129
    ? 'Местонахождение (место осуществления деятельности)'
    : 'Местонахождение';

  const handleObjectTypeChange = useCallback(
    (option: IDictionarySubjectTypeSelectOption) => {
      if (!hasId) {
        objectTypeChangeCallback && objectTypeChangeCallback(option);
        // @ts-ignore
        formRef?.current?.change(`${name}.objectKind`);
        // @ts-ignore
        formRef?.current?.change(`${name}.objectSubKind`);
      }
    },
    [objectTypeChangeCallback, formRef, hasId],
  );

  return (
    <KnmCollapse
      id={id}
      key={id}
      theme="fields"
      title={`Объект №${index + 1}`}
      subtitle={generateCollapseName}
      isNew={isNewObject}
      opened={isOpen}
      headerClassName={cn([
        {
          [styles.BorderYellow]:
            inspectionResultAvailable &&
            inspectionResultAvailable.resultActionType === InspectionResultActionTypeEnum.LIGHT_YELLOW,
          [styles.BorderRed]:
            inspectionResultAvailable &&
            inspectionResultAvailable.resultActionType === InspectionResultActionTypeEnum.LIGHT_RED,
        },
      ])}
      onToggle={toggle}
      openByDefault={isNewObject}
      onClose={(!isWatchOnly || isLinkedByImpossibility) && !isFilledFromPrescriptionLink ? onRemove : undefined}
      headerPostfix={
        <Tooltip
          placement="left"
          type="history"
          content={<div className="shared-tooltip-info-content">Отсутствует связь с КНО и видом контроля в ЕРВК</div>}
        >
          <ErvkCorrectionRequiredInfo checkForControlObjects />
        </Tooltip>
      }
      additionalButtons={
        <ErknmApproveRemarkChanges sectionCode={ErknmApproveRemarkSectionCodeEnum.OBJECTS} guid={value.guid} />
      }
      error={isKnmCollapseOpened && hasError}
      changeOpenedCallback={setIsKnmCollapseOpened}
    >
      {isOpen ? (
        <>
          {!isUseCosIdentifiers && value.ervkId && !value.cosId && isProsecutorOrSuperProsecutor && (
            <Row>
              <FieldBlock
                label="Номер объекта"
                placementLabel="left"
                color="black"
                labelSize="col-4"
                id={`${id}.tmpErvkCosId`}
              >
                <TextInputField name={tmpErvkCosIdFieldName} required disabled={true} className="shared-row-field" />
                <TextareaField className={styles.AddressField} name={addressFieldName} />
              </FieldBlock>
            </Row>
          )}
          {!isUseCosIdentifiers && value.ervkId && !value.cosId && !isProsecutorOrSuperProsecutor && (
            <Row>
              <FieldBlock
                label="Номер объекта"
                placementLabel="left"
                color="black"
                labelSize="col-4"
                id={`${id}.ervkNumber`}
              >
                <TextInputField name={ervkNumberFieldName} required disabled className="shared-row-field" />
                <TextareaField className={styles.AddressField} name={addressFieldName} />
              </FieldBlock>
            </Row>
          )}
          {(isUseCosIdentifiers || value.cosId) && hasId && (
            <Row>
              <FieldBlock
                label={<>Номер объекта</>}
                placementLabel="left"
                color="black"
                labelSize="col-4"
                id={`${id}.cosId`}
                tooltip={
                  lateFieldName && (
                    <KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/ervkNumber`} fieldId={`${id}.ervkNumber`} />
                  )
                }
              >
                {value.cosId ? (
                  <TextInputFieldAsLink
                    name={ervkNumberFieldName}
                    className="shared-row-field"
                    link={`https://ervk.gov.ru/objects/${value.cosId}`}
                  />
                ) : (
                  <TextInputField name={`${name}.ervkNumber`} required disabled className="shared-row-field" />
                )}
                <TextareaField className={styles.AddressField} name={addressFieldName} />
              </FieldBlock>
            </Row>
          )}
          {hasId && (
            <Row>
              <FieldBlock
                label={<>Наименование объекта</>}
                placementLabel="left"
                color="black"
                labelSize="col-4"
                id={`${id}.name`}
                tooltip={
                  lateFieldName && (
                    <KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/name`} fieldId={`${id}.name`} />
                  )
                }
              >
                <TextInputField
                  name={objectNameFieldName}
                  required
                  disabled
                  className="shared-row-field"
                  inspectionResult={inspectionResultName}
                />
                <TextareaField className={styles.AddressField} name={addressFieldName} />
              </FieldBlock>
            </Row>
          )}
          <Row>
            <FieldBlock
              label={getTitleFieldWithRedStar({
                label: placeLabel,
                field: addressFieldName,
                isPm,
                isKnm: isErknm,
                isDecision: !isPm && !isErknm,
              })}
              placementLabel="left"
              color="black"
              labelSize="col-4"
              id={`${id}.address`}
              tooltip={
                lateFieldName && (
                  <KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/address`} fieldId={`${id}.address`} />
                )
              }
            >
              <KnmAddressField
                id={addressFieldName}
                addressFieldPathWithPrefix={addressFieldName}
                required
                clearable={value.autocomplete}
                disabled={isAddressDisabled}
                onAddressFieldBlur={handleAddressFieldBlur}
                onClear={handleAutocompleteClear}
                inspectionResult={inspectionResultAddress}
                formRef={formRef}
                prefixNameNotInFias={`${name}.`}
                knmSystemType={knmSystemType}
                className="shared-row-field"
                manualAddressPlaceholder={
                  <>
                    Место
                    <ErknmFieldMark fieldName={`${name}.manualAddress`} />
                    {lateFieldName && (
                      <KnmLateFieldsRemark
                        lateFieldPath={`${lateFieldName}/manualAddress`}
                        fieldId={addressFieldName}
                      />
                    )}
                  </>
                }
                regionPlaceholder={
                  <>
                    Субъект РФ
                    <ErknmFieldMark fieldName={`${name}.region`} />
                    {lateFieldName && (
                      <KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/region`} fieldId={`${id}.region`} />
                    )}
                  </>
                }
                isRegionTextField={false}
                hideManualAddress={hasId}
                hideNotInFiasCheckbox={!isLaterThanHideFields4129 && hasId}
                lateFieldName={lateFieldName}
              />
              <TextareaField className={styles.AddressField} name={addressFieldName} />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock
              label={getTitleFieldWithRedStar({
                label: 'Тип объекта',
                field: `${name}.objectType`,
                isPm,
                isKnm: isErknm,
                isDecision: !isPm && !isErknm,
              })}
              placementLabel="left"
              color="black"
              labelSize="col-4"
              id={`${id}.objectType`}
              tooltip={
                <>
                  <Tooltip
                    placement="right"
                    type="info"
                    content={
                      <div className="shared-tooltip-info-content">
                        Поле зависит от значения в следующем поле:
                        <ul>
                          <p>Контрольный (надзорный) орган</p>
                          <p>Вид контроля (надзора)</p>
                          <p>{TooltipPartText()}</p>
                        </ul>
                      </div>
                    }
                  >
                    <span className="shared-tooltip-info-icon" />
                  </Tooltip>
                  {lateFieldName && (
                    <KnmLateFieldsRemark
                      lateFieldPath={`${lateFieldName}/objectType/title`}
                      fieldId={`${id}.objectType`}
                    />
                  )}
                </>
              }
            >
              <SelectInputField
                className="shared-row-field"
                name={`${name}.objectType`}
                options={objectTypesSelectOptions}
                disabled={isObjectTypeDisabled}
                onChange={handleObjectTypeChange}
                size="medium"
                validate={composeValidators(
                  makeRequired('Поле не может быть пустым'),
                  makeProductionObjectTypeValidator(
                    addOnlyProductionObject,
                    'Допустимо добавление только производственного объекта',
                  ),
                )}
              />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock
              label={getTitleFieldWithRedStar({
                label: 'Вид объекта',
                field: `${name}.objectKind`,
                isPm,
                isKnm: isErknm,
                isDecision: !isPm && !isErknm,
              })}
              placementLabel="left"
              color="black"
              labelSize="col-4"
              id={`${id}.objectKind`}
              tooltip={
                <>
                  <Tooltip
                    placement="right"
                    type="info"
                    content={
                      <div className="shared-tooltip-info-content">
                        Поле зависит от значения в следующем поле:
                        <ul>
                          <p>Контрольный (надзорный) орган</p>
                          <p>Вид контроля (надзора)</p>
                          <p>{TooltipPartText()}</p>
                          <p>Тип объекта</p>
                        </ul>
                      </div>
                    }
                  >
                    <span className="shared-tooltip-info-icon" />
                  </Tooltip>
                  {lateFieldName && (
                    <KnmLateFieldsRemark
                      lateFieldPath={`${lateFieldName}/objectKind/title`}
                      fieldId={`${id}.objectKind`}
                    />
                  )}
                </>
              }
            >
              <SelectInputField
                className="shared-row-field"
                name={`${name}.objectKind`}
                options={hasId ? objectKindsSelectOptions : objectKindsSelectOptionsByObjectType}
                disabled={isObjectKindDisabled}
                size="medium"
                validate={makeRequired('Поле не может быть пустым')}
                onChange={handleObjectKindChange}
                warning={!hasId && !value.objectType ? 'Укажите тип объекта' : undefined}
              />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock
              label={getTitleFieldWithRedStar({
                label: 'Подвид объекта',
                field: `${name}.objectSubKind`,
                isPm,
                isKnm: isErknm,
                isDecision: !isPm && !isErknm,
              })}
              placementLabel="left"
              color="black"
              labelSize="col-4"
              id={`${id}.objectSubKind`}
              tooltip={
                <>
                  <Tooltip
                    placement="right"
                    type="info"
                    content={
                      <div className="shared-tooltip-info-content">
                        Поле зависит от значения в следующем поле:
                        <ul>
                          <p>Контрольный (надзорный) орган</p>
                          <p>Вид контроля (надзора)</p>
                          <p>{TooltipPartText()}</p>
                          <p>Тип объекта</p>
                          <p>Вид объекта</p>
                        </ul>
                      </div>
                    }
                  >
                    <span className="shared-tooltip-info-icon" />
                  </Tooltip>
                  {lateFieldName && (
                    <KnmLateFieldsRemark
                      lateFieldPath={`${lateFieldName}/objectSubKind/title`}
                      fieldId={`${id}.objectSubKind`}
                    />
                  )}
                </>
              }
            >
              <SelectInputField
                className="shared-row-field"
                name={`${name}.objectSubKind`}
                options={hasId ? objectSubKindsSelectOptions : objectSubKindsSelectOptionsByObjectType}
                disabled={isObjectSubKindDisabled}
                size="medium"
                warning={!hasId && !value.objectType ? 'Укажите тип объекта' : undefined}
              />
            </FieldBlock>
          </Row>
          {((!value.ervkId && isHideRiskCategoryOrDangerClass && !hazardClassApplies) ||
            !isHideRiskCategoryOrDangerClass ||
            (value.ervkId && !hazardClassApplies)) && (
            <Row>
              <FieldBlock
                label={
                  <>
                    Категория риска
                    <ErknmFieldMark fieldName={`${name}.riskCategory`} />
                  </>
                }
                placementLabel="left"
                color="black"
                labelSize="col-4"
                id={`${id}.riskCategory`}
                tooltip={
                  <>
                    <Tooltip
                      placement="right"
                      type="info"
                      content={
                        isUseOnlyLowRiskInManualObject && !value.ervkId ? (
                          <div className="shared-tooltip-info-content">
                            <p className={styles.LowRiskTooltip}>
                              Вручную можно добавить только объекты с низкой категорией риска. Объекты с другой
                              категорией риска должны быть добавлены из ЕРВК
                            </p>
                          </div>
                        ) : (
                          <div className="shared-tooltip-info-content">
                            {isUseOnlyLowRiskInManualObject && !value.ervkId ? (
                              <p className={styles.LowRiskTooltip}>
                                Вручную можно добавить только объекты с низкой категорией риска. Объекты с другой
                                категорией риска должны быть добавлены из ЕРВК
                              </p>
                            ) : (
                              <>
                                Поле зависит от значения в следующем поле:
                                <ul>
                                  <p>Контрольный (надзорный) орган</p>
                                  <p>Вид контроля (надзора)</p>
                                  <p>{TooltipPartText()}</p>
                                  {isErknm && (
                                    <>
                                      <p>Характер мероприятия</p>
                                      <p>Вид мероприятия (Если КНМ плановая)</p>
                                    </>
                                  )}
                                </ul>
                              </>
                            )}
                          </div>
                        )
                      }
                    >
                      <span className="shared-tooltip-info-icon" />
                    </Tooltip>
                    {lateFieldName && (
                      <KnmLateFieldsRemark
                        lateFieldPath={`${lateFieldName}/riskCategory/title`}
                        fieldId={`${id}.riskCategory`}
                      />
                    )}
                  </>
                }
              >
                <SelectInputField
                  className="shared-row-field"
                  name={`${name}.riskCategory`}
                  options={riskCategoriesSelectOptions}
                  disabled={isRiskCategoryDisabled}
                  size="medium"
                />
              </FieldBlock>
            </Row>
          )}
          {shouldShowDangerClass && (
            <Row>
              <FieldBlock
                label={
                  <>
                    Класс опасности
                    <ErknmFieldMark fieldName={`${name}.dangerClass`} />
                  </>
                }
                placementLabel="left"
                color="black"
                labelSize="col-4"
                id={`${id}.dangerClass`}
                tooltip={
                  <>
                    <Tooltip
                      placement="right"
                      type="info"
                      content={
                        <div className="shared-tooltip-info-content">
                          Поле зависит от значения в следующем поле:
                          <ul>
                            <p>Контрольный (надзорный) орган</p>
                            <p>Вид контроля (надзора)</p>
                            <p>{TooltipPartText()}</p>
                            {isErknm && (
                              <>
                                <p>Характер мероприятия</p>
                                <p>Вид мероприятия (Если КНМ плановая)</p>
                              </>
                            )}
                          </ul>
                        </div>
                      }
                    >
                      <span className="shared-tooltip-info-icon" />
                    </Tooltip>
                    {lateFieldName && (
                      <KnmLateFieldsRemark
                        lateFieldPath={`${lateFieldName}/dangerClass/title`}
                        fieldId={`${id}.dangerClass`}
                      />
                    )}
                  </>
                }
              >
                <SelectInputField
                  className="shared-row-field"
                  options={dangerClassesSelectOptions}
                  name={`${name}.dangerClass`}
                  disabled={isDangerClassDisabled}
                  size="medium"
                />
              </FieldBlock>
            </Row>
          )}
          {!value.isNew && !!value.cosId && (
            <Row>
              <div>
                <Button
                  className={styles.SignButton}
                  text="Перезапросить"
                  color="primary"
                  id="refreshButton"
                  type="button"
                  onClick={refresh}
                  disabled={isRefreshButtonDisabled || (!!updatingTime && !isUpdatingTimeOut)}
                />
                <div>
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {updatingTime ? (
                    isUpdatingTimeOut ? (
                      <>
                        <Hint text={RELOAD_OBJECT_BY_ERVK_TIME_OUT} />
                        <br />
                        <Hint text={RELOAD_OBJECT_WARNING} />
                      </>
                    ) : (
                      <Hint text={RELOAD_OBJECT_BY_ERVK} />
                    )
                  ) : (
                    <Hint text={RELOAD_OBJECT_WARNING} />
                  )}
                </div>
              </div>
            </Row>
          )}
        </>
      ) : (
        '...'
      )}
    </KnmCollapse>
  );
};

const mapStateToProps = (state: IRootState): IErknmObjectStoreProps => ({
  decisionDataCreated: getDecisionDataCreatedAt(state),
  decisionId: getDecisionDataId(state),
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  knmId: getKnmDataId(state),
  settings: getAdminFetchSettings(state),
});

export const KnmObjectItem = connect(mapStateToProps)(KnmObjectWithoutStore);

export function makeProductionObjectTypeValidator<T>(
  addOnlyProductionObject: boolean | undefined,
  errorMsg: T,
  getException?: () => boolean,
) {
  return (value: IDictionarySubjectTypeSelectOption) => {
    const isException = getException && getException();

    if (isException) {
      return undefined;
    }

    if (addOnlyProductionObject && value.code !== '3') {
      return errorMsg;
    }

    return undefined;
  };
}
